import React, { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAppConfig } from "../ConfigContext";
import "../index.css";

function normalizeCurrency(value) {
  const s = String(value || "").toLowerCase();

  if (s === "coin_1" || s === "sparks" || s === "buysparks") return "sparks";
  if (s === "coin_2" || s === "runes" || s === "buyrunes") return "runes";

  return "sparks";
}

function CurrencyPurchaseModal({
  open,
  currency = "sparks",
  user,
  setUser,
  onClose,
}) {
  const { shop } = useAppConfig();

  const normalizedCurrency = normalizeCurrency(currency);

  useEffect(() => {
  if (!open) return;

  setSelectedIdx(null);
  setLoading(false);
  setSuccessOpen(false);
  setSuccessAmount(null);
  setErrorOpen(false);
  setErrorMessage("");
}, [open, normalizedCurrency]);

  const [selectedIdx, setSelectedIdx] = useState(null);
  const [loading, setLoading] = useState(false);

  const [successOpen, setSuccessOpen] = useState(false);
  const [successAmount, setSuccessAmount] = useState(null);

  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const isTMA =
    typeof window !== "undefined" && !!window.Telegram?.WebApp;

  /*
    IMPORTANT:
    This follows your current Shop mapping:
    - coin_1 / Buy Sparks uses shop.runes
    - coin_2 / Buy Runes uses shop.sparks
  */
const skus = useMemo(() => {
  if (normalizedCurrency === "sparks") return shop?.runes ?? [];
  return shop?.sparks ?? [];
}, [shop, normalizedCurrency]);

  const selectedSku = selectedIdx !== null ? skus[selectedIdx] : null;

  const title =
    normalizedCurrency === "sparks" ? "Buy Sparks" : "Buy Runes";

  const headerImage =
    normalizedCurrency === "sparks" ? "/coin_2.png" : "/coin_1.png";

  const amountIcon =
    normalizedCurrency === "sparks" ? "/sparks.png" : "/runes.png";

  const costIcon =
    normalizedCurrency === "sparks" ? "/runes.png" : "/tstar.svg";

  const emptyText =
    normalizedCurrency === "sparks"
      ? "No spark packs configured."
      : "No rune packs configured.";

  const closeAll = () => {
    setSelectedIdx(null);
    setLoading(false);
    setSuccessOpen(false);
    setSuccessAmount(null);
    setErrorOpen(false);
    setErrorMessage("");
    onClose?.();
  };

  const refreshUserFromApi = async () => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/me`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    if (!res.ok) return null;

    return res.json();
  };

  const purchaseCurrency = async () => {
  if (!selectedSku) return;

  const res = await fetch(
    `${process.env.REACT_APP_API_URL}/api/shop/purchase-currency`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({
        type: "sparks",
        amount: selectedSku.amount,
        cost: selectedSku.price,
      }),
    }
  );

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data?.error || "Purchase failed");
  }

  setUser?.((prev) => ({
    ...prev,
    xp: data.user?.xp ?? prev?.xp,
    runes: data.user?.runes ?? prev?.runes,
    sparks: data.user?.sparks ?? prev?.sparks,
  }));

  return normalizedCurrency === "sparks"
    ? selectedSku.price
    : selectedSku.amount;
};

const purchaseRunesWithStars = async () => {
  const sku = selectedSku;
  if (!sku) return;

  if (!isTMA || !window.Telegram?.WebApp?.openInvoice) {
    throw new Error(
      "Runes can only be purchased with Telegram Stars inside the Telegram app."
    );
  }

  const res = await fetch(
    `${process.env.REACT_APP_API_URL}/api/shop/stars/create-order`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({
        amount: sku.amount,
        price: sku.price,
      }),
    }
  );

  const data = await res.json();

  if (!res.ok || !data.ok) {
    throw new Error(data?.error || "Could not start Telegram Stars payment");
  }

  const { orderId, invoiceLink } = data;

  return new Promise((resolve, reject) => {
    window.Telegram.WebApp.openInvoice(invoiceLink, async (status) => {
      try {
        if (status !== "paid") {
          reject(
            new Error(
              status === "cancelled" ? "Payment cancelled" : "Payment failed"
            )
          );
          return;
        }

        let tries = 0;
        const maxTries = 20;
        const wait = (ms) => new Promise((r) => setTimeout(r, ms));

        while (tries < maxTries) {
          const chk = await fetch(
            `${process.env.REACT_APP_API_URL}/api/telegram/stars/order/${orderId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );

          if (chk.ok) {
            const j = await chk.json();
            if (j.status === "paid") break;
          }

          tries++;
          await wait(1500);
        }

        const meRes = await fetch(`${process.env.REACT_APP_API_URL}/api/me`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (meRes.ok) {
          const me = await meRes.json();

          setUser?.((prev) => ({
            ...prev,
            sparks: me.sparks ?? prev?.sparks,
            runes: me.runes ?? prev?.runes,
            xp: me.xp ?? prev?.xp,
          }));
        } else {
          setUser?.((prev) => ({
            ...prev,
            runes: (prev?.runes || 0) + Number(sku.amount || 0),
          }));
        }

        resolve(sku.amount);
      } catch (err) {
        reject(err);
      }
    });
  });
};

  const confirmPurchase = async () => {
  if (!selectedSku || loading) return;

  setLoading(true);

  try {
    const amount =
      normalizedCurrency === "runes"
        ? await purchaseRunesWithStars()
        : await purchaseCurrency();

    setSuccessAmount(amount);
    setSuccessOpen(true);
    setSelectedIdx(null);
  } catch (err) {
    console.error("Currency purchase failed:", err);
    setErrorMessage(err?.message || "Purchase failed");
    setErrorOpen(true);
  } finally {
    setLoading(false);
  }
};

  if (!open) return null;

  return (
    <AnimatePresence>
      <div className="modal_box" onClick={closeAll}>

        <div className="modal_title">Currency Purchase
          <button className="modal_close" onClick={closeAll}/>
        </div>
        
        <motion.div
          className="modal_body"
          onClick={(e) => e.stopPropagation()}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
        >
          {!successOpen && !errorOpen && (
            <>
              <div className="modal_inside">

              <div className="shadow2" style={{fontWeight: 600, fontSize: 28, marginBottom: 10,marginTop: -10}}>
                {title}
              </div>

              {skus.length === 0 && (
                <div
                  style={{
                    color: "#ddd",
                    textShadow: "1px 1px #000",
                    marginBottom: 12,
                  }}
                >
                  {emptyText}
                </div>
              )}

              <div
  className="shadow1"
  style={{
    fontWeight: 600,
    fontSize: 18,
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
  }}
>
  Current Balance:
  <img
    src={normalizedCurrency === "sparks" ? "/sparks.png" : "/runes.png"}
    alt=""
    style={{ height: 28, width: 28, objectFit: "contain",filter: "drop-shadow(0 1px 0 rgba(0,0,0,0.9))",}}
  />
  {normalizedCurrency === "sparks" ? user?.sparks : user?.runes}
</div>

              <div style={{ float: "left", width: "100%",marginBottom: -10 }}>
                {skus.map((sku, idx) => {
                  const isChecked = selectedIdx === idx;

                  const displayAmount =
                  normalizedCurrency === "sparks" ? sku.price : sku.amount;

                const displayCost =
                  normalizedCurrency === "sparks" ? sku.amount : sku.price;

                  const rawPrice = Number(sku.price || 0);
                  const discountedPrice = Number(sku.discountStars || 0);
                  const finalPrice =
                    normalizedCurrency === "runes" && discountedPrice > 0
                      ? discountedPrice
                      : rawPrice;

                  const hasDiscount =
                    normalizedCurrency === "runes" &&
                    discountedPrice > 0 &&
                    discountedPrice < rawPrice;

                  return (
                    <motion.div
                      key={`${normalizedCurrency}-${idx}`}
                      className="info"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: idx * 0.05 }}
                      onClick={() => setSelectedIdx(idx)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "10px",
                        marginBottom: 0,
                        paddingRight: 0,
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          width: "200px",
                          gap: "12px",
                        }}
                      >
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedIdx(idx);
                          }}
                          style={{
                            backgroundImage: `url('${
                              isChecked
                                ? "/checkedbox.png"
                                : "/uncheckedbox.png"
                            }')`,
                            width: 26,
                            height: 26,
                            backgroundSize: "cover",
                            display: "inline-block",
                            cursor: "pointer",
                          }}
                        />

                        <div
                          className="amount"
                          style={{
                            fontSize: 21,
                            backgroundImage: `url('${amountIcon}')`,
                            backgroundSize: "28px 28px",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "left center",
                            paddingLeft: 32,
                            color: "#fff",
                            textShadow: "1px 1px #000",
                          }}
                        >
                          {displayAmount}
                        </div>
                      </div>

                      <div
  className="amount ms-auto"
  style={{
    marginLeft: "auto",
    paddingRight: 14,
    fontSize: 21,
    color: "#fff",
    textShadow: "1px 1px #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    minWidth: 120,
  }}
>
                        {hasDiscount && (
                          <span
                            style={{
                              textDecoration: "line-through",
                              opacity: 0.6,
                              fontSize: 18,
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <img src="/tstar.svg" alt="" style={{ height: 18 }} />
                            {rawPrice}
                          </span>
                        )}

                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <img
                            src={costIcon}
                            alt=""
                            style={{
                              height: normalizedCurrency === "runes" ? 24 : 28,
                              width: normalizedCurrency === "runes" ? "auto" : 28,
                              objectFit: "contain",
                            }}
                          />
                          {displayCost}
                        </span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
              </div>

              <div className="modal_footer">

              <div
                onClick={selectedSku && !loading ? confirmPurchase : undefined}
                className="game-button green-button"
              >
                {loading
  ? "PROCESSING..."
  : normalizedCurrency === "runes"
    ? "PAY WITH TELEGRAM STARS"
    : "CONFIRM PURCHASE"}
              </div>
              </div>
            </>
          )}

          {successOpen && (
            <>
              <div className="modal_inside">

              <img
                src="/finger.png"
                alt="Success"
                style={{ height: 80, marginBottom: 10 }}
              />

              <div className="shadow2" style={{fontWeight: 600, fontSize: 26, marginBottom: 15 }}>
                Transaction Completed
              </div>

              <div
                className="shadow1"
                style={{
                  lineHeight: "24px",
                  marginBottom: 10,
                  fontSize: 18,
                  fontWeight: 600
                }}
              >
                <strong style={{color: "#32cd32"}}>
                  {successAmount}{" "}
                  {normalizedCurrency === "sparks" ? "Sparks" : "Runes"}
                </strong>{" "}
                Successfully Added To Your Account!
              </div>
              </div>

              <div className="modal_footer">
                <button className="game-button red-button" onClick={closeAll}>
                  CLOSE
                </button>
              </div>
            </>
          )}

          {errorOpen && (
            <>
              <div className="modal_inside">

              <img src="/finger.png" style={{height: 80,marginBottom: 10, transform: "rotate(180deg)"}}/>
              
              <div className="shadow2" style={{fontWeight: 600, fontSize: 28, marginBottom: 12 }}>
                Purchase Failed
              </div>

              <div
              className="shadow1"
                style={{
                  fontWeight: 600,
                  fontSize: 18,
                  lineHeight: "24px",
                }}
              >
                {errorMessage}
              </div>
              </div>

              <div className="modal_footer">
                <button
                className="game-button green-button"
                  onClick={() => {
                    setErrorOpen(false);
                    setErrorMessage("");
                  }}
                  style={{marginRight: 10 }}
                >
                  Try Again
                </button>

                <button className="game-button red-button" onClick={closeAll}>
                  CLOSE
                </button>
              
              </div>
            </>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

export default CurrencyPurchaseModal;